import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import ParallaxBanner from '../components/ParallaxBanner';
import contentService from '../services/contentService';
import featureFlags from '../utils/featureFlags';
import useContentUpdates from '../hooks/useContentUpdates';
import { authService } from '../services/authService';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [error, setError] = useState('');
  const [isGoogleAccount, setIsGoogleAccount] = useState(false);

  // Handle page loader fadeout
  useEffect(() => {
    const handleWindowLoad = () => {
      if (window.$ && window.$('.loader1').length > 0) {
        window.$('.loader1').fadeOut(800);
        window.$('.side-menu').removeClass('hidden');
      }
    };

    if (document.readyState === 'complete') {
      handleWindowLoad();
    } else {
      window.addEventListener('load', handleWindowLoad);
    }

    return () => {
      window.removeEventListener('load', handleWindowLoad);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setError('');
    setIsGoogleAccount(false);

    try {
      await authService.forgotPassword(email);
      setStatus('success');
    } catch (err) {
      // Check for rate limiting
      if (err.status === 429) {
        const retryAfter = err.data?.retryAfter || 900;
        setError(`Too many requests. Please try again in ${Math.ceil(retryAfter / 60)} minutes.`);
        setStatus('error');
      }
      // Check for Google-only account
      else if (err.message?.includes('Google') || err.data?.message?.includes('Google')) {
        setIsGoogleAccount(true);
        setError('This account uses Google Sign-In. Please use the "Sign in with Google" button.');
        setStatus('error');
      }
      // For security, show success even if email doesn't exist
      else {
        setStatus('success');
      }
    }
  };


  const [backgroundUrl, setBackgroundUrl] = useState(null);

  const fetchBackground = useCallback(async () => {
    if (!featureFlags.useDynamicBackgrounds()) {
      return;
    }
    try {
      const result = await contentService.getPageBackground('product-listing');
      if (result.success && result.imageUrl) {
        setBackgroundUrl(result.imageUrl);
      }
    } catch (err) {
      console.error('[ForgotPassword] Error fetching background:', err);
    }
  }, []);

  useEffect(() => {
    fetchBackground();
  }, [fetchBackground]);

  useContentUpdates({
    onPageBackgroundUpdated: (page) => {
      if (page === 'product-listing') {
        fetchBackground();
      }
    }
  });

  return (
    <Layout>
      {/* Parallax Banner */}
      {backgroundUrl && (
        <ParallaxBanner
          imageUrl={backgroundUrl}
          speed={0.6}
          offset={-100}
          className="paralax-section-slide-data1"
        />
      )}

      {/* Main Content */}
      <section className="login wide-tb-100">
        <div className="container">
          <div className="row">
            <div className="offset-lg-3 col-lg-6 col-md-12 col-sm-12 whitebox">
              <div className="widget logincontainer">

                {status === 'success' ? (
                  /* Success Message */
                  <div className="text-center">
                    <div className="alert alert-success" role="alert">
                      <h4 className="alert-heading">Check your email</h4>
                      <p>
                        If an account exists with <strong>{email}</strong>, we've sent a password reset link.
                      </p>
                      <hr />
                      <p className="mb-0">
                        Didn't receive the email? Check your spam folder or{' '}
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={() => setStatus('idle')}
                        >
                          try again
                        </button>
                      </p>
                    </div>
                    <Link to="/login" className="btn btn-default mt-3">
                      Back to Login
                    </Link>
                  </div>
                ) : (
                  /* Form */
                  <>
                    <h3 className="bottom35 text-center">Forgot Password</h3>
                    <p className="text-center text-muted mb-4">
                      Enter your email address and we'll send you a link to reset your password.
                    </p>

                    {error && (
                      <div className="alert alert-danger" role="alert">
                        {error}
                        {isGoogleAccount && (
                          <div className="mt-3">
                            <Link to="/login" className="btn btn-outline-primary btn-sm">
                              Go to Login Page
                            </Link>
                          </div>
                        )}
                      </div>
                    )}

                    <form className="getin_form border-form" onSubmit={handleSubmit}>
                      <div className="row">
                        <div className="col-md-12 col-sm-12">
                          <div className="form-group">
                            <input
                              type="email"
                              name="email"
                              className="form-control"
                              placeholder="Email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              required
                              disabled={status === 'loading'}
                            />
                          </div>
                        </div>

                        <div className="col-sm-12">
                          <button
                            type="submit"
                            className="form-btn btn-theme bg-navy-blue"
                            disabled={status === 'loading'}
                          >
                            {status === 'loading' ? 'Sending...' : 'Send Reset Link'}
                          </button>
                        </div>
                      </div>
                    </form>

                    <p className="text-center top20">
                      Remember your password? <Link to="/login">Sign In</Link>
                    </p>
                    <p className="text-center">
                      Don't have an account? <Link to="/register">Sign Up</Link>
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default ForgotPassword;
